// The finance tables' closed vocabularies. The request schemas and the tables'
// CHECK constraints read these same lists. No imports: the schema reads this
// file as it is.

/** What kind of account, which decides what it admits: a ledger, positions,
 *  one balance or several. A `deposit` is money a bank holds locked — a rent
 *  deposit, a term deposit — and is not spendable. */
export const ACCOUNT_TYPES = [
  "current",
  "savings",
  "deposit",
  "credit_card",
  "prepaid_card",
  "cash",
  "brokerage",
  "pension",
] as const;

/** What kind of institution. `self` holds what the person holds themselves. */
export const INSTITUTION_TYPES = ["bank", "broker", "pension", "self", "other"] as const;

/** How the application treats an asset — a currency, a security, a coin — and
 *  so how it rounds, values and lots it. Not a financial classification. */
export const ASSET_TYPES = ["fiat", "security", "crypto"] as const;

/** How a stated fact reached the database. */
export const RECORD_ORIGINS = ["imported", "manual", "fetched"] as const;

export const TRADE_SIDES = ["buy", "sell"] as const;

export const CATEGORY_TYPES = ["primary", "secondary"] as const;

/** The categories the application routes on, by what they are rather than
 *  what they are called. `pillar_2` and `pillar_3a` are secondaries. */
export const SYSTEM_KEYS = [
  "investment",
  "transfer",
  "salary",
  "dividend",
  "interest",
  "other",
  "health",
  "taxes",
  "work",
  "pillar_2",
  "pillar_3a",
] as const;

/** How a learned rule matches a description: the whole of it, or a word in it. */
export const RULE_TYPES = ["exact", "keyword"] as const;

/** Which of a movement's dates a stated balance counts movements by: the day
 *  the institution booked them (the ledger's own day), the day they were
 *  traded, or their value date, which for cash is the settlement date. One
 *  entry per date the ledger stores on a movement, since a figure is read
 *  through the movements by that date. */
export const BALANCE_BASES = ["booked", "traded", "value"] as const;

/** What a balance the application maintains is worked out from. */
export const DERIVED_BY = ["holdings"] as const;

/**
 * What Data quality checks for, in the order it lists them:
 *
 * - `unreconciled` — the movements between two stated figures do not carry
 *   one to the other, or a brokerage's stated `Invested` is not what its
 *   holdings were worth that day;
 * - `negative-cash` — a brokerage's cash, worked out from its movements, goes
 *   below zero;
 * - `no-balance` — nothing has ever been stated for the account;
 * - `statement-due` — an extension states the balance, and a statement that
 *   can exist has not been imported;
 * - `payslip-due` — a salary arrived on the account a week ago or more, in a
 *   month no payslip states;
 * - `not-updated` — the person states the balance, and no figure is recent
 *   enough.
 */
export const DATA_QUALITY_CONDITIONS = [
  "unreconciled",
  "negative-cash",
  "no-balance",
  "statement-due",
  "payslip-due",
  "not-updated",
] as const;

/** The one thing to do about a condition. `review` reads the account's
 *  transactions, `review-balances` its balances. */
export const DATA_QUALITY_ACTIONS = [
  "review",
  "review-balances",
  "add-balance",
  "import",
  "update",
] as const;
